// Job 75: "Parse Status add to bills Menu, Add Dates Filter".
//
// Parse state was visible in two places, both of which require you to already
// know where to look: a badge per row in the Inbox, and the Invoices tab of one
// site. Neither answers "what failed to parse this month" without opening rows
// one at a time. This is that question as a page.
//
// ── Where the state comes from ──────────────────────────────────────────────
// The badge, the reason and the elapsed time all come from utils/parseState,
// the same functions the Inbox and the site Invoices tab use. That is
// deliberate: 'stalled' is a time-derived state, and a second copy of the rule
// living in this page would drift from the one behind the other two surfaces.
//
// The server filters on the stored `parsing_status` column only, so the chip a
// person clicks and the badge they then read are computed at different layers.
// They agree for the four stored states. They cannot for 'stalled', which has
// no column — so there is no Stalled chip, and stalled rows appear under
// Parsing or Pending (whichever they hold) with the flag the table gives them.
// Saying that on screen is cheaper than a filter that quietly disagrees with
// its own results.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FileWarning, Search, AlertTriangle, CheckCircle2, Clock, Loader2,
  RefreshCw, ChevronLeft, ChevronRight, X, Mail, ExternalLink,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { DateRangeFilter } from '../components/common/DateRangeFilter';
import { billEmailService, type InboxEmail } from '../services/billEmailService';
import {
  parseStateOf, parseErrorOf, activeParseElapsedMs, formatElapsed,
  hasActiveParse, type ParseState,
} from '../utils/parseState';

const PAGE_SIZE = 25;
/** How often to re-poll while any row on the page is genuinely mid-parse. */
const POLL_MS = 4000;

// ─── Chips ────────────────────────────────────────────────────────────────────

// `value` is the `parsing_status` sent to the server, so these are exactly the
// states that can be filtered. 'stalled' is absent by design — see the header.
type Chip = { key: string; label: string; value?: string; countKey?: string };

const CHIPS: Chip[] = [
  { key: 'all',     label: 'All',     countKey: 'all' },
  { key: 'failed',  label: 'Failed',  value: 'failed',  countKey: 'failed' },
  { key: 'parsing', label: 'Parsing', value: 'parsing', countKey: 'parsing' },
  { key: 'pending', label: 'Pending', value: 'pending', countKey: 'pending' },
  { key: 'parsed',  label: 'Parsed',  value: 'parsed',  countKey: 'parsed' },
];

const STATE_META: Record<ParseState, { label: string; cls: string; icon: typeof Clock }> = {
  none:    { label: 'No attachment', cls: 'bg-gray-100 text-gray-500',      icon: FileWarning },
  pending: { label: 'Pending',       cls: 'bg-gray-100 text-gray-600',      icon: Clock },
  parsing: { label: 'Parsing',       cls: 'bg-blue-100 text-blue-700',      icon: Loader2 },
  stalled: { label: 'Stalled',       cls: 'bg-amber-100 text-amber-800',    icon: AlertTriangle },
  parsed:  { label: 'Parsed',        cls: 'bg-green-100 text-green-700',    icon: CheckCircle2 },
  failed:  { label: 'Failed',        cls: 'bg-red-100 text-red-700',        icon: AlertTriangle },
};

function StateBadge({ email }: { email: InboxEmail }) {
  const state = parseStateOf(email);
  const { label, cls, icon: Icon } = STATE_META[state];
  const elapsed = activeParseElapsedMs(email);
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', cls)}>
      <Icon className={cn('h-3 w-3', state === 'parsing' && 'animate-spin')} />
      {label}
      {/* Ticks along with the poll, so a live parse is visibly moving rather
          than a frozen word — the complaint behind job 68. */}
      {elapsed != null && <span className="tabular-nums opacity-70">{formatElapsed(elapsed)}</span>}
    </span>
  );
}

/**
 * Why it failed, or why a parse that started is being called stalled.
 *
 * `parse_error` is written for the EMAIL path only. A manual upload that fails
 * persists nothing (the reason is returned in the HTTP response and dropped —
 * billValidation.ts's parse-invoice route), which is the open half of job 76.
 * This page lists email-sourced invoices, so the reason is available here; the
 * fallback text covers a parse that died before it could record one.
 */
function Reason({ email }: { email: InboxEmail }) {
  const state = parseStateOf(email);
  if (state !== 'failed' && state !== 'stalled') return null;

  const text = parseErrorOf(email)
    ?? 'The parse stopped without recording a reason — most likely the server '
      + 'restarted mid-parse. Retry to run it again.';

  return (
    <p
      className={cn('mt-1 max-w-[38rem] text-[11px] leading-snug break-words',
        state === 'failed' ? 'text-red-600' : 'text-amber-700')}
      title={text}
    >
      {text}
    </p>
  );
}

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

export function ParseStatusPage() {
  const [rows, setRows] = useState<InboxEmail[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chip, setChip] = useState('failed');   // the reason anyone opens this page
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryingAll, setRetryingAll] = useState(false);
  // Ticked rows, by email id. Held across pagination on purpose: pasting twenty
  // NMIs can span pages, and losing the ticks on page 2 would make the feature
  // useless for exactly the case it exists for.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [retryingSelected, setRetryingSelected] = useState(false);
  // Outcome of a bulk retry. Not an error and not a row — the run is still going
  // when this appears, so it says what was queued rather than what happened.
  const [notice, setNotice] = useState<string | null>(null);

  const activeChip = CHIPS.find((c) => c.key === chip) ?? CHIPS[0];

  // The only date here is when the email arrived, so the date control renders
  // its basis as a fixed label rather than a selector.
  const load = useCallback(async (opts: { quiet?: boolean } = {}) => {
    if (!opts.quiet) setLoading(true);
    const [list, countRes] = await Promise.all([
      billEmailService.listInbox({
        parse: activeChip.value,
        search: search.trim() || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
      billEmailService.getParseCounts({ from: dateFrom || undefined, to: dateTo || undefined }),
    ]);

    if (list.success) {
      setRows(list.data ?? []);
      setTotal(list.total ?? 0);
      setError(null);
    } else {
      setError(list.error ?? 'Failed to load parse status');
    }
    if (countRes.success) setCounts(countRes.data ?? {});
    if (!opts.quiet) setLoading(false);
  }, [activeChip.value, search, dateFrom, dateTo, page]);

  useEffect(() => { void load(); }, [load]);

  // Poll only while something on this page is genuinely mid-parse. hasActiveParse
  // excludes 'stalled' on purpose — a dead parse will not resolve on its own, and
  // polling for it forever is a request every few seconds for as long as the tab
  // is open.
  useEffect(() => {
    if (!hasActiveParse(rows)) return;
    const t = setInterval(() => { void load({ quiet: true }); }, POLL_MS);
    return () => clearInterval(t);
  }, [rows, load]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, lastPage);

  const hasFilters = !!(search || dateFrom || dateTo || chip !== 'all');

  const stalledOnPage = useMemo(
    () => rows.filter((r) => parseStateOf(r) === 'stalled').length,
    [rows],
  );

  const onRetry = async (email: InboxEmail) => {
    setRetryingId(email.id);
    const res = await billEmailService.retryParse(email.id);
    if (!res.success) setError(res.error ?? 'Retry failed');
    setRetryingId(null);
    await load({ quiet: true });
  };

  // Re-parse the whole failed backlog. The server queues the work and parses a
  // few at a time, so this returns almost immediately and the counts move as the
  // run progresses — refreshed once here to pick up the first transitions, and
  // by Refresh thereafter.
  const onRetryAll = async () => {
    setRetryingAll(true);
    setNotice(null);
    // Same date bounds the list and the counts use, so "Retry all failed (12)"
    // under a date filter re-parses those 12 and not the whole backlog.
    const res = await billEmailService.retryAllFailed({
      from: dateFrom || undefined,
      to: dateTo || undefined,
    });
    setRetryingAll(false);

    if (!res.success) {
      setError(res.error ?? 'Bulk retry failed');
      return;
    }

    const { retrying, skipped, concurrency } = res.data ?? { retrying: 0, skipped: 0, concurrency: 0 };
    setNotice(
      retrying === 0
        ? skipped > 0
          ? `Nothing to retry — ${skipped} failed ${skipped === 1 ? 'invoice is' : 'invoices are'} not assigned to a bill period.`
          : 'Nothing to retry.'
        : `Re-parsing ${retrying} invoice${retrying === 1 ? '' : 's'}, ${concurrency} at a time. `
          + `This runs in the background — use Refresh to follow it.`
          + (skipped > 0 ? ` ${skipped} skipped (not assigned to a bill period).` : ''),
    );
    await load({ quiet: true });
  };

  // Re-parse just the ticked rows. Anything not already parsed is eligible —
  // a stalled or never-started row is a legitimate thing to tick.
  const onRetrySelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;

    setRetryingSelected(true);
    setNotice(null);
    const res = await billEmailService.retrySelected(ids);
    setRetryingSelected(false);

    if (!res.success) {
      setError(res.error ?? 'Retry failed');
      return;
    }

    const { retrying, skipped, concurrency } = res.data ?? { retrying: 0, skipped: 0, concurrency: 0 };
    setNotice(
      retrying === 0
        ? skipped > 0
          ? `Nothing to retry — the selected ${skipped === 1 ? 'invoice is' : 'invoices are'} not assigned to a bill period.`
          : 'Nothing to retry in the selection — those invoices have already parsed.'
        : `Re-parsing ${retrying} selected invoice${retrying === 1 ? '' : 's'}, ${concurrency} at a time.`
          + ` Use Refresh to follow it.`
          + (skipped > 0 ? ` ${skipped} skipped (not assigned to a bill period).` : ''),
    );
    setSelected(new Set());
    await load({ quiet: true });
  };

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Header checkbox acts on the current page only — it cannot tick rows the user
  // cannot see, and with a filter applied "all" would otherwise be ambiguous
  // about whether it meant the page or the whole result set.
  const pageIds = rows.map((r) => r.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const resetPage = () => setPage(1);

  return (
    <div data-testid="parse-status-page" className="w-full py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileWarning className="h-6 w-6 text-primary" />
            Parse Status
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which invoice PDFs parsed, which did not, and why
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Selection wins the primary slot while it exists — it is the more
              specific action, and the one the user just expressed intent for. */}
          {selected.size > 0 && (
            <Button
              size="sm"
              className="gap-1.5"
              data-testid="parse-status-retry-selected"
              onClick={() => void onRetrySelected()}
              disabled={retryingSelected || loading}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', retryingSelected && 'animate-spin')} />
              {retryingSelected ? 'Starting…' : `Retry selected (${selected.size})`}
            </Button>
          )}
          {/* Scoped to the Failed filter. The action re-parses every failed
              invoice in the organisation, so it belongs beside the list of them
              — offered while "Parsed" is selected it reads as though it would
              act on what is on screen, which is the opposite of what it does.
              Hidden with no backlog, and disabled while a run is being queued so
              a double-click cannot start two. */}
          {chip === 'failed' && (counts.failed ?? 0) > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="parse-status-retry-all"
              onClick={() => void onRetryAll()}
              disabled={retryingAll || loading}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', retryingAll && 'animate-spin')} />
              {retryingAll ? 'Starting…' : `Retry all failed (${counts.failed})`}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            data-testid="parse-status-refresh"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {notice && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 text-blue-500 hover:text-blue-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-0">
          {/* Chips. Counts are of attachments, rows are of emails, so the two
              numbers differ wherever an email carries more than one PDF — said
              on the chips rather than left to be discovered. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {CHIPS.map((c) => {
              const n = c.countKey ? counts[c.countKey] : undefined;
              return (
                <button
                  key={c.key}
                  data-testid={`parse-status-chip-${c.key}`}
                  data-active={chip === c.key}
                  onClick={() => { setChip(c.key); resetPage(); }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    chip === c.key
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                  )}
                  title={n != null ? `${n} attachment${n === 1 ? '' : 's'}` : undefined}
                >
                  {c.label}
                  {n != null && <span className="tabular-nums opacity-60">{n}</span>}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              {/* Several NMIs at once, comma- or newline-separated, so a column
                  pasted out of a spreadsheet finds all of those invoices. A
                  plain phrase is still matched whole — see parseSearchTerms. */}
              <Input
                data-testid="parse-status-search"
                placeholder="Search sender, subject, or paste multiple NMIs…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                className="pl-9 h-9 w-80 text-sm"
              />
            </div>
            {/* No basis selector: an invoice email has exactly one date. The
                Bills list offers the choice because it shows two. */}
            <DateRangeFilter
              basis="created"
              basisLabel="Received"
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => { setDateFrom(f); setDateTo(t); resetPage(); }}
            />
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-gray-500 gap-1"
                onClick={() => {
                  setSearch(''); setDateFrom(''); setDateTo(''); setChip('all'); resetPage();
                }}
              >
                <X className="h-3.5 w-3.5" />
                Clear filters
              </Button>
            )}
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            Dates filter on when the email arrived — an invoice email has no billing period of its own.
            {stalledOnPage > 0 && (
              <>
                {' '}
                <span className="text-amber-700">
                  {stalledOnPage} row{stalledOnPage === 1 ? '' : 's'} on this page started parsing and never
                  finished; they are counted under Parsing or Pending because that is the status they still hold.
                </span>
              </>
            )}
          </p>
        </CardHeader>

        <CardContent className="p-0 mt-4">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <AlertTriangle className="h-10 w-10 mb-3 text-red-400" />
              <p className="text-sm">{error}</p>
              <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => void load()}>
                Try again
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mb-3 opacity-20" />
              <p className="text-sm font-medium">
                {chip === 'failed' ? 'Nothing failed to parse' : 'No invoices match these filters'}
              </p>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setChip('all'); resetPage(); }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table data-testid="parse-status-table" className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-3 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded-[4px] border-gray-300 cursor-pointer accent-primary"
                          checked={pageAllSelected}
                          onChange={togglePage}
                          disabled={rows.length === 0}
                          aria-label="Select all rows on this page"
                        />
                      </th>
                      <th className="px-6 py-3">Received</th>
                      <th className="px-6 py-3">From / Subject</th>
                      <th className="px-6 py-3">Site</th>
                      <th className="px-6 py-3">Parse</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((email) => {
                      const state = parseStateOf(email);
                      const attCount = (email.attachments ?? []).length;
                      return (
                        <tr
                          key={email.id}
                          data-testid="parse-status-row"
                          data-email-id={email.id}
                          data-parse-state={state}
                          className={cn(
                            'hover:bg-gray-50/50 align-top',
                            selected.has(email.id) && 'bg-blue-50/50',
                          )}
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded-[4px] border-gray-300 cursor-pointer accent-primary"
                              checked={selected.has(email.id)}
                              onChange={() => toggleRow(email.id)}
                              aria-label={`Select ${email.subject || 'invoice email'}`}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground">
                            {fmtWhen(email.received_at)}
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-800 flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                              {email.sender_name || email.sender_email}
                            </p>
                            <p className="text-xs text-muted-foreground max-w-[26rem] truncate" title={email.subject ?? ''}>
                              {email.subject || '(no subject)'}
                            </p>
                            {attCount > 1 && (
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {attCount} attachments — the badge reflects the worst of them
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {email.site
                              ? (
                                <Link
                                  to={`/bill-validation/sites/${email.site.id}`}
                                  className="font-mono text-gray-700 hover:text-primary hover:underline"
                                  title="Open site"
                                >
                                  {email.site.nmi ?? email.site.name ?? 'site'}
                                </Link>
                              )
                              : <span className="text-gray-400">Unassigned</span>}
                          </td>
                          <td className="px-6 py-4">
                            <StateBadge email={email} />
                            <Reason email={email} />
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="inline-flex gap-1.5">
                              {/* Retry is offered for the two states it can help:
                                  a recorded failure, and a parse that died. It is
                                  hidden mid-parse so a second run cannot be
                                  stacked on the same attachment. */}
                              {(state === 'failed' || state === 'stalled') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 gap-1 text-xs"
                                  disabled={retryingId === email.id}
                                  onClick={() => void onRetry(email)}
                                >
                                  <RefreshCw className={cn('h-3 w-3', retryingId === email.id && 'animate-spin')} />
                                  {retryingId === email.id ? 'Retrying…' : 'Retry parse'}
                                </Button>
                              )}
                              <Link
                                to="/bill-validation/inbox"
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 h-7 text-xs text-gray-600 hover:bg-gray-50"
                                title="Open the Inbox to assign, preview or delete this email"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Inbox
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-xs text-gray-500">
                <span>
                  {total} email{total === 1 ? '' : 's'}
                  {activeChip.value ? ` with a ${activeChip.label.toLowerCase()} attachment` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm" className="h-8 px-2"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, Math.min(p, lastPage) - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>Page {safePage} of {lastPage}</span>
                  <Button
                    variant="outline" size="sm" className="h-8 px-2"
                    disabled={safePage >= lastPage}
                    onClick={() => setPage((p) => Math.min(lastPage, Math.min(p, lastPage) + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
